/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation } from "@i18n";
import { useEffect, useRef, useState, useCallback, type ChangeEvent } from "react";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import AddUserModal from "@Components/SpaceSettings/UserAdministration/ReviewFlows/ReviewFlowReviewLevels/AddUserModal";
import ReviewStageTransitionChart from "./ReviewStageTransitionChart";
import UserNameTagComponent from "@CubeComponents/html/UserNameTagComponent";
import AutoComplete from "@CubeComponents/Mui_Form/AutoComplete";
import { Checkbox, TextField, Box } from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import IconComponent from "@CubeComponents/icons";
import Text from "@CubeComponents/html/Text/Text";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

interface ReviewNotificationModalProps {
  readonly modalTitle?: string;
  readonly confirmBtnTitle?: string;
  readonly targetLevelReviewers?: any[];
  readonly targetLevelSerial?: any;
  readonly spaceId?: any;
  readonly cancelCallback?: any;
  readonly confirmCallback?: any;
  readonly approvalStages?: any[];
  readonly action?: any;
  readonly requireMandatoryComment?: boolean;
  readonly skipReviewTillLevel?: any;
  readonly rollbackReviewToLevel?: any;
  readonly isLoadingApprove?: any;
  readonly setIsLoadingApprove?: any;
  readonly checkBoxToAssignUserToTransition?: any;
}

function ReviewNotificationModal({
  modalTitle = "Send Notification",
  confirmBtnTitle = "Confirm",
  targetLevelReviewers = [],
  targetLevelSerial,
  spaceId,
  cancelCallback,
  confirmCallback,
  approvalStages = [],
  action,
  requireMandatoryComment = false,
  skipReviewTillLevel,
  rollbackReviewToLevel,
  isLoadingApprove,
  setIsLoadingApprove,
}: ReviewNotificationModalProps) {
  const { t } = useTranslation();
  const initialTargetLevelReviewerIds = targetLevelReviewers.map((record: any) => record.userId);

  const defaultReviewNotificationData = {
    selectedReviewersAtTargetLevel: initialTargetLevelReviewerIds,
    selectedReviewersFromSpace: [],
    message: "",
  };

  const [modalData, setModalData] = useState<any>(defaultReviewNotificationData);
  const [spaceUsersModal, setSpaceUsersModal] = useState<any>({ show: false });
  const [selectedReviewers, setSelectedReviewers] = useState<any[]>(targetLevelReviewers);
  const notificationMultiSelectRef = useRef<any>({ value: [] });
  const [disabledIds, setDisabledIds] = useState<any>([]);

  const messageOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    setModalData((prev: any) => ({ ...prev, message: e.target.value }));
  };

  const closeAddUserModal = () => {
    setSpaceUsersModal({ show: false });
  };

  const openUsersInSpaceList = () => {
    setSpaceUsersModal({ show: true });
  };

  const addUserCallback = (selectedUsers: any) => {
    const selectedUsersMapper = selectedUsers.map(({ userId, firstName, lastName, profilePic }: any) => ({
      userId,
      firstName,
      lastName,
      profilePic,
    }));

    const currentOtherSpaceUsers = modalData.selectedReviewersFromSpace;
    const currentOtherSpaceUserIds = currentOtherSpaceUsers.map((user: any) => user.userId);
    const currentLevel1UsersIds = selectedReviewers.map((r: any) => r.userId);

    const totalCurrentUserIds = new Set([...currentLevel1UsersIds, ...currentOtherSpaceUserIds]);

    const newUsersToAdd = selectedUsersMapper.filter((user: any) => !totalCurrentUserIds.has(user.userId));
    const totalUsersInOtherSpaceList = [...currentOtherSpaceUsers, ...newUsersToAdd];

    setModalData((prev: any) => ({
      ...prev,
      selectedReviewersFromSpace: totalUsersInOtherSpaceList,
    }));
    closeAddUserModal();
  };

  const removeReviewerFromLevelNotificationList = useCallback((userId: any) => {
    setSelectedReviewers((prev: any[]) => {
      const updated = prev.filter((u: any) => u.userId !== userId);
      notificationMultiSelectRef.current.value = updated.map((u: any) => u.userId);
      return updated;
    });

    setModalData((prevModal: any) => ({
      ...prevModal,
      selectedReviewersAtTargetLevel: prevModal.selectedReviewersAtTargetLevel.filter((id: any) => id !== userId),
    }));
  }, []);

  const removeReviewerFromSpaceNotificationList = useCallback((userId: any) => {
    setModalData((prev: any) => ({
      ...prev,
      selectedReviewersFromSpace: prev.selectedReviewersFromSpace.filter((user: any) => user.userId !== userId),
    }));
  }, []);

  const confirmNotificationHandler = () => {
    if (typeof setIsLoadingApprove === "function") {
      setIsLoadingApprove(true);
    }

    const { message, selectedReviewersFromSpace } = modalData;

    const selectedReviewerIdsAtTargetLevel = selectedReviewers.map((r: any) => r.userId);
    const selectedReviewerIdsFromSpace = selectedReviewersFromSpace.map((user: any) => user.userId);

    confirmCallback({
      message,
      selectedReviewerIdsFromSpace,
      selectedReviewerIdsAtTargetLevel,
    });
  };

  useEffect(() => {
    if (targetLevelSerial && targetLevelReviewers?.length) {
      notificationMultiSelectRef.current.value = initialTargetLevelReviewerIds;
    }
  }, []);

  const getDefaultUserNameComponent = (user: any) => {
    return (
      <UserNameTagComponent
        id={user.userId}
        handleDeleteIconClicked={() => removeReviewerFromSpaceNotificationList(user.userId)}
      />
    );
  };

  const handleSelect = useCallback((event: any, newValue: any[]) => {
    const updatedSelected = newValue.filter((item: any) => typeof item === "object" && item.userId);

    setSelectedReviewers(updatedSelected);

    const selectedUserIds = new Set(updatedSelected.map((u: any) => u.userId));
    const isNotSelected = (user: any) => !selectedUserIds.has(user.userId);

    setModalData((prev: any) => ({
      ...prev,
      selectedReviewersAtTargetLevel: updatedSelected.map((u: any) => u.userId),
      selectedReviewersFromSpace: prev.selectedReviewersFromSpace?.filter(isNotSelected),
    }));
  }, []);

  const notificationHeader = () => <p className="w-full text-2xl font-bold rounded-t-2">{modalTitle}</p>;

  const renderOptionItem = useCallback(
    (props: any, option: any, { selected }: any) => (
      <li {...props} id={`itemTemplate_user_${option.userId}`}>
        <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginInlineEnd: "0.5rem" }} checked={selected} />
        <UserNameTagComponent id={option.userId} />
      </li>
    ),
    [],
  );

  const renderTagItem = useCallback(
    (option: any) => (
      <Box key={option.userId} className="flex flex-row items-center pt-1" id={`valueTemplate_user_${option.userId}`}>
        <UserNameTagComponent
          id={option.userId}
          handleDeleteIconClicked={() => removeReviewerFromLevelNotificationList(option.userId)}
        />
      </Box>
    ),
    [removeReviewerFromLevelNotificationList],
  );

  const renderInputField = useCallback(
    (params: any) => <TextField {...params} variant="outlined" placeholder={t("Reviewers")} />,
    [t],
  );

  const getReviewerMultiSelect = useCallback(
    () => (
      <AutoComplete
        multiple
        disableCloseOnSelect
        options={targetLevelReviewers}
        value={selectedReviewers}
        onChange={handleSelect}
        disablePortal
        getOptionLabel={(option: any) => `${option.firstName ?? ""} ${option.lastName ?? ""}`.trim()}
        isOptionEqualToValue={(opt: any, val: any) => opt.userId === val.userId}
        renderOption={renderOptionItem}
        renderTags={(value: any[]) => value.map(renderTagItem)}
        renderInput={renderInputField}
      />
    ),
    [targetLevelReviewers, selectedReviewers, handleSelect, renderOptionItem, renderTagItem, renderInputField],
  );

  useEffect(() => {
    setDisabledIds([
      ...modalData.selectedReviewersFromSpace.map((user: any) => user.userId),
      ...modalData.selectedReviewersAtTargetLevel,
    ]);
  }, [modalData]);

  const notificationContent = () => {
    const isRollback = action === "rollbackReview";

    return (
      <div className="p-0">
        <div className="pt-8 pb-8 ps-8 pe-4 border-b-2 border-system_colour_44 mx-auto">
          {approvalStages.length ? (
            <ReviewStageTransitionChart
              approvalStages={approvalStages}
              isRollback={isRollback}
              skipReviewTillLevel={skipReviewTillLevel}
              rollbackReviewToLevel={rollbackReviewToLevel}
            />
          ) : undefined}
        </div>
        {targetLevelSerial && (
          <div
            className="py-6 px-8 pb-4 flex flex-col gap-4 justify-start border-b-0.5 border-[#ecf3fe] last:border-b-0 [&_p]:text-base [&_p]:font-[var(--font-weight-600)] [&_span]:text-xs [&_span]:font-[var(--font-weight-400)]"
            id="reviewsAutoNotificationWrapper"
          >
            <p>
              <Text>{t("Notify Reviewers at Level {{level}}", { level: targetLevelSerial })}</Text>
            </p>
            {getReviewerMultiSelect()}
            <span>
              <Text>
                {t(
                  "Email Notifications are only sent to reviewers listed above. Any reviewer for this task can take action during the review task.",
                )}
              </Text>
            </span>
          </div>
        )}

        <div className="py-6 px-8 pb-4 flex flex-col gap-4 justify-start border-b-0.5 border-[#ecf3fe] last:border-b-0 [&_p]:text-base [&_p]:font-[var(--font-weight-600)] [&_span]:text-xs [&_span]:font-[var(--font-weight-400)]">
          <p>
            <Text>{t("Notify Other Members of the Space")}</Text>
          </p>
          <div>
            <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={openUsersInSpaceList}>
              <div className="flex gap-1 items-center">
                <IconComponent name="add" width="1rem" height="1rem" />
                <Text>{t("Add")}</Text>
              </div>
            </ButtonComponent>
          </div>
          <div className="flex flex-row gap-2 flex-wrap items-center justify-start">
            {modalData.selectedReviewersFromSpace.map((user: any) => getDefaultUserNameComponent(user))}
          </div>
          <span>
            <Text>{t("Notified space members can read review comments.")}</Text>
          </span>
        </div>

        <div className="py-6 px-8 pb-4 flex flex-col gap-4 justify-start border-b-0.5 border-[#ecf3fe] last:border-b-0 [&_p]:text-base [&_p]:font-[var(--font-weight-600)] [&_span]:text-xs [&_span]:font-[var(--font-weight-400)]">
          {requireMandatoryComment ? (
            <p>
              <Text>{t("Custom Message*")}</Text>
            </p>
          ) : (
            <p>
              <Text>{t("Custom Message")}</Text>
            </p>
          )}
          <TextFieldComponent
            multiline
            value={modalData.message}
            placeholder={t("Enter notification message")}
            rows={5}
            height="auto"
            onChange={messageOnChange}
          />
        </div>
      </div>
    );
  };

  const notificationFooter = () => (
    <div className="flex flex-row gap-4 items-center justify-start">
      <ButtonComponent
        onClick={confirmNotificationHandler}
        buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
        disabled={requireMandatoryComment && !modalData.message.trim()}
        loading={isLoadingApprove}
      >
        {confirmBtnTitle}
      </ButtonComponent>
      <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={cancelCallback}>
        <Text>{t("Cancel")}</Text>
      </ButtonComponent>
    </div>
  );

  return (
    <>
      <ModalStateDialogComponent
        isVisible
        allowModalPadding={false}
        width="62.5rem"
        headerTitle={notificationHeader()}
        onCancel={cancelCallback}
        modelContent={notificationContent()}
        footerContent={notificationFooter()}
      />

      {spaceUsersModal.show && (
        <AddUserModal
          spaceId={spaceId}
          addUserCallback={addUserCallback}
          closeModal={closeAddUserModal}
          disabledUserIds={disabledIds}
          checkBoxToAssignUserToTransition={undefined}
        />
      )}
    </>
  );
}

export default ReviewNotificationModal;
